import { useMemo } from "react";
import { GROUP_MARKER } from "../constants";
import type { GroupTuple, ListboxChild, Row } from "../types";

function isGroup<Value>(child: ListboxChild<Value>): child is GroupTuple<Value> {
  return child[0] === GROUP_MARKER;
}

/**
 * Flattens the tuples Autocomplete hands the listbox into one row per line the
 * virtualizer has to place, and works out the ARIA set each option belongs to.
 */
function useListboxRows<Value>(children: React.ReactNode) {
  return useMemo(() => {
    const listboxChildren = children as unknown as ListboxChild<Value>[];
    const rows: Row<Value>[] = [];
    const optionRowIndex: number[] = [];
    const groupRowIndexes: number[] = [];
    const ungrouped: Extract<Row<Value>, { kind: "option" }>[] = [];

    listboxChildren.forEach((child) => {
      if (isGroup(child)) {
        const groupRowIndex = rows.length;
        groupRowIndexes.push(groupRowIndex);
        const groupRow: Extract<Row<Value>, { kind: "group" }> = {
          kind: "group",
          key: child[1],
          label: child[2],
          lastRowIndex: groupRowIndex,
        };
        rows.push(groupRow);
        child[3].forEach((tuple, indexInGroup) => {
          optionRowIndex.push(rows.length);
          rows.push({
            kind: "option",
            key: tuple[0].key,
            tuple,
            optionIndex: optionRowIndex.length - 1,
            groupRowIndex,
            // An option inside a group belongs to that group's set, not the
            // whole list.
            positionInSet: indexInGroup + 1,
            setSize: child[3].length,
          });
        });
        groupRow.lastRowIndex = rows.length - 1;
      } else {
        optionRowIndex.push(rows.length);
        const row: Extract<Row<Value>, { kind: "option" }> = {
          kind: "option",
          key: child[0].key,
          tuple: child,
          optionIndex: optionRowIndex.length - 1,
          groupRowIndex: -1,
          positionInSet: optionRowIndex.length,
          setSize: 0,
        };
        rows.push(row);
        ungrouped.push(row);
      }
    });

    // Ungrouped options belong to the whole list, whose length is only known
    // once it is built. Virtualization means the DOM never shows all of it.
    ungrouped.forEach((row) => {
      row.setSize = optionRowIndex.length;
    });

    return { rows, optionRowIndex, groupRowIndexes };
  }, [children]);
}

export default useListboxRows;
